import { createAsyncThunk } from "@reduxjs/toolkit"
import { message } from "antd"
import dayjs from "dayjs"

import i18n from "../../../i18n.ts"
import type { RootState } from "../../../store.ts"
import {
  downloadBlob,
  filenameFromContentDisposition
} from "../../../utils/download-file.ts"
import {
  type AnalyticsQueryParams,
  type ExportQueryParams,
  type FilterQueryParams,
  type GlobalMapFacetsResponse,
  type ListQueryParams,
  exportListRows as exportListRowsRequest,
  getAnalytics,
  getFacets,
  getLeadQuickView,
  getListRows,
  getMapFeatures,
  getProjectQuickView,
  getRecentActivity
} from "../api/global-map.api"
import {
  LA_FALLBACK,
  globalMapSliceName,
  recordLayers
} from "./global-map.constants"
import { isRecordEntity } from "./global-map.types"
import {
  type AnalyticsResponse,
  type EntityQuickView,
  type EntityType,
  type MapLayer,
  type GlobalMapFacets,
  type GlobalMapFilters,
  type GlobalMapListResponse,
  type ListEntityTab,
  type ListSortField,
  type MapFeaturesResponse,
  type MapViewport,
  type RecentActivityByEntityResponse
} from "./global-map.types"

interface GlobalMapError {
  code: string
  retryable: boolean
}

// Production errors go through `rest_framework_custom_exception_handler`,
// which nests a raised error's own `code` under `response.data.details.code`
// — there is no top-level `code` in that envelope. The top-level fallback is
// kept only in case some other error path ever answers flatter than that.
const toGlobalMapError = (response: {
  data?: unknown
  problem: string | null
}): GlobalMapError => {
  const data = response.data as
    | { code?: string; details?: { code?: string } }
    | undefined

  return {
    code: data?.details?.code ?? data?.code ?? "request_failed",
    retryable: response.problem !== "CLIENT_ERROR"
  }
}

// One spec per `GlobalMapFilters` field: the backend's snake_case query
// param name, plus how to turn that field's own value into the string the
// wire wants (or `undefined` to omit it — "absent" and `""`/`[]` mean
// different things to the backend, so unset/cleared fields must never be
// sent at all). A plain hand-written if-chain (this used to be one) type-
// checks each individual condition but can't catch a whole field missing
// from it — a future new `GlobalMapFilters` field could silently never reach
// the request (Finding 7, Increment 3 final whole-branch review). The
// `satisfies`-typed mapped object below is the same compile-time
// exhaustiveness gate `FIELD_LABEL_KEYS`/`FILTER_PARAMS` already use
// elsewhere in this feature: omit or misspell a field here and this fails to
// compile instead of silently under-building the request.
const FILTER_PARAM_SPECS = {
  salespersonIds: {
    param: "salesperson",
    toValue: (value: string[]) => (value.length ? value.join(",") : undefined)
  },
  projectManagerIds: {
    param: "project_manager",
    toValue: (value: string[]) => (value.length ? value.join(",") : undefined)
  },
  teamMemberIds: {
    param: "team_member",
    toValue: (value: string[]) => (value.length ? value.join(",") : undefined)
  },
  subcontractorIds: {
    param: "subcontractor",
    toValue: (value: string[]) => (value.length ? value.join(",") : undefined)
  },
  projectStatusIds: {
    param: "project_status",
    toValue: (value: number[]) => (value.length ? value.join(",") : undefined)
  },
  leadStatusIds: {
    param: "lead_status",
    toValue: (value: number[]) => (value.length ? value.join(",") : undefined)
  },
  projectTypeIds: {
    param: "type",
    toValue: (value: string[]) => (value.length ? value.join(",") : undefined)
  },
  region: {
    param: "region",
    toValue: (value: string[]) => (value.length ? value.join(",") : undefined)
  },
  activePeriodStart: {
    param: "active_period_start",
    toValue: (value) => value || undefined
  },
  activePeriodEnd: {
    param: "active_period_end",
    toValue: (value) => value || undefined
  },
  nearZip: {
    param: "near_zip",
    toValue: (value) => value || undefined
  },
  nearRadiusMi: {
    param: "near_radius_mi",
    toValue: (value) => (value == null ? undefined : String(value))
  },
  hasUpcomingAppointment: {
    param: "has_upcoming_appointment",
    toValue: (value) => (value ? "true" : undefined)
  },
  includeHistory: {
    param: "include_history",
    toValue: (value) => (value ? "true" : undefined)
  }
} as const satisfies {
  // `-?` strips the optional modifier `{[K in keyof T]: ...}` would otherwise
  // preserve from GlobalMapFilters' 7 optional fields — without it, omitting
  // a field here compiles silently instead of failing, defeating the whole
  // point of this exhaustiveness gate.
  [K in keyof GlobalMapFilters]-?: {
    param: keyof FilterQueryParams
    toValue: (value: GlobalMapFilters[K]) => string | undefined
  }
}

// Only non-empty fields are ever sent — see `FILTER_PARAM_SPECS` above for
// why. `search` is included only for `fetchMapFeatures` — callers building
// facets params pass an empty string.
const buildFilterParams = (
  filters: GlobalMapFilters,
  search: string
): FilterQueryParams & { search?: string } => {
  const params: FilterQueryParams & { search?: string } = {}

  ;(Object.keys(FILTER_PARAM_SPECS) as (keyof GlobalMapFilters)[]).forEach(
    (key) => {
      const spec = FILTER_PARAM_SPECS[key]
      // Iterating the mapped type generically loses the per-key correlation
      // between `filters[key]`'s value type and that key's own `toValue`
      // signature that `FILTER_PARAM_SPECS`'s `satisfies` clause guarantees at
      // authoring time — this cast is the one place that trusts it still
      // holds, same shape `useGlobalMapUrlSync.ts`'s equivalent table needs.
      const value = (spec.toValue as (value: unknown) => string | undefined)(
        filters[key]
      )
      if (value !== undefined) params[spec.param] = value
    }
  )

  if (search) params.search = search

  return params
}

// `viewport` is null on the very first load, before the map has reported its
// bounds — the request goes out unbounded (server falls back to its own
// default center) rather than waiting on a viewport that may never arrive.
//
// `entities` is the "Projects & Leads" layer toggle (`activeLayers` in
// `useMapFeatures.ts`) — 2026-09-19: the layer toggle became a real
// server-side filter (previously client-side only, which is why the KPI
// totals used to stay frozen when a layer was switched off — the backend
// had already been made entities-aware, but no request ever carried the
// param). The thunk's own `condition` below refuses to fire at all when
// `entities` is empty (the 0-of-2-layers state `toggleLayer` deliberately
// allows) — the backend's `entities` field 400s on an empty value rather
// than defaulting to "both", so there is no valid request to send; the
// existing client-side `filterByActiveLayers` in `MapTab.tsx` already
// reduces whatever `data` is currently held down to zero features/clusters
// for that case.
export const fetchMapFeatures = createAsyncThunk<
  MapFeaturesResponse,
  {
    viewport: MapViewport | null
    filters: GlobalMapFilters
    search: string
    entities: MapLayer[]
    // The List tab's "Show on Map" bulk action — when set, every other
    // field on this params object is still accepted (the `condition` guard
    // below still needs `entities`) but sent alongside `ids` for shape
    // consistency only; the backend ignores them entirely once `ids` is
    // present (see `getMapFeatures`'s own doc comment). Never combined
    // client-side with the viewport/filters — `ids` mode is a one-shot
    // override, not a narrowing on top of the current committed query.
    ids?: string[]
    /** The selected branch workspace, when one is active. */
    team?: string
  },
  { rejectValue: GlobalMapError }
>(
  `${globalMapSliceName}/fetchMapFeatures`,
  async (
    { viewport, filters, search, entities, ids, team },
    { rejectWithValue }
  ) => {
    const viewportParams = viewport
      ? {
          north: viewport.north,
          south: viewport.south,
          east: viewport.east,
          west: viewport.west,
          zoom: viewport.zoom
        }
      : { zoom: LA_FALLBACK.zoom }

    // `ids` mode sends nothing but `ids` + a `zoom` the backend requires but
    // never reads for this branch — no viewport/filters/entities, since the
    // backend ignores them anyway and sending them would misleadingly imply
    // they still narrow the result.
    const params = ids?.length
      ? { zoom: viewportParams.zoom, ids }
      : {
          ...viewportParams,
          ...buildFilterParams(filters, search),
          entities: entities.join(","),
          ...(team ? { team } : {})
        }

    const response = await getMapFeatures(params)

    if (response.ok) return response.data as MapFeaturesResponse

    // One request carries every layer, so a backend that refuses ONE of them
    // refuses the whole map — markers, clusters, counts and all — and the map
    // goes dark behind a fatal banner. That is what a deploy window looks
    // like from the browser: `ezbuild_front` ships the Subcontractor and
    // Appointment layers, `ezbuild_back` has not yet, and its `entities`
    // field answers a flat 400 for a value it does not know.
    //
    // A layer failing is not the map failing. When the refusal is the
    // server's own (a 4xx) and the request named a derived layer, it is asked
    // again for the record layers alone — the ones every backend has always
    // understood — and the answer says which layers were refused, so the map
    // keeps working and the legend can show those two as unavailable rather
    // than switched on with nothing drawn.
    //
    // One retry, inside the same call: no second dispatch, no state change
    // between the two requests, so no effect can re-enter and no refetch loop
    // can form.
    const refusedByServer = response.problem === "CLIENT_ERROR"
    const derivedRequested = entities.filter((layer) => !isRecordEntity(layer))
    const recordRequested = entities.filter(isRecordEntity)

    if (
      !ids?.length &&
      refusedByServer &&
      derivedRequested.length &&
      recordRequested.length
    ) {
      const retry = await getMapFeatures({
        ...params,
        entities: recordRequested.join(",")
      })

      if (retry.ok) {
        return {
          ...(retry.data as MapFeaturesResponse),
          unavailable_layers: derivedRequested
        }
      }
    }

    return rejectWithValue(toGlobalMapError(response))
  },
  { condition: ({ entities, ids }) => entities.length > 0 || !!ids?.length }
)

// The backend answers with its own snake_case field names for the three
// multi-word facets (see `GlobalMapFacetsResponse`'s comment in
// `global-map.api.ts`) — this is the one place that reshapes the wire
// response into `GlobalMapFacets`'s camelCase shape, so every reader
// downstream (`GlobalMapFilterBar`, `FilterChips`, `active-filter-summary.ts`)
// can keep using plain camelCase field access.
const mapFacetsResponse = (
  response: GlobalMapFacetsResponse
): GlobalMapFacets => ({
  status: response.status,
  projectType: response.project_type,
  region: response.region,
  salesperson: response.salesperson,
  projectManager: response.project_manager,
  teamMember: response.team_member,
  subcontractor: response.subcontractor
})

// Facet option counts are filter-aware (per this plan's facets design) so
// they reflect the same active filters the map itself is querying with — but
// they take no free-text search term, so `search` is always built empty.
export const fetchFacets = createAsyncThunk<
  GlobalMapFacets,
  GlobalMapFilters,
  { rejectValue: GlobalMapError }
>(`${globalMapSliceName}/fetchFacets`, async (filters, { rejectWithValue }) => {
  const params = buildFilterParams(filters, "")

  const response = await getFacets(params)

  if (!response.ok) return rejectWithValue(toGlobalMapError(response))

  return mapFacetsResponse(response.data as GlobalMapFacetsResponse)
})

// The List tab's synchronized row feed — same shared filters as
// `fetchMapFeatures`/`fetchFacets` (via `buildFilterParams`) plus the List
// tab's own layer toggle (`listEntity`, distinct from the map's `entities`
// layer selection, which still gates this thunk the same way it gates
// `fetchMapFeatures`: 2026-09-19's entities-aware KPI-totals fix's own
// `condition` guard, mirrored here for the identical reason — a zero-layer
// selection has no valid `entities` value to send, and the backend 400s on
// an empty one rather than defaulting to "both".
export const fetchListRows = createAsyncThunk<
  GlobalMapListResponse,
  {
    filters: GlobalMapFilters
    search: string
    entities: EntityType[]
    listEntity: ListEntityTab
    sort: ListSortField
    direction: "asc" | "desc"
    page: number
    pageSize: number
    /** The selected branch workspace, when one is active. */
    team?: string
  },
  { rejectValue: GlobalMapError }
>(
  `${globalMapSliceName}/fetchListRows`,
  async (
    {
      filters,
      search,
      entities,
      listEntity,
      sort,
      direction,
      page,
      pageSize,
      team
    },
    { rejectWithValue }
  ) => {
    const params: ListQueryParams = {
      ...buildFilterParams(filters, search),
      entities: entities.join(","),
      list_entity: listEntity,
      sort,
      direction,
      page,
      page_size: pageSize,
      ...(team ? { team } : {})
    }

    const response = await getListRows(params)

    if (!response.ok) return rejectWithValue(toGlobalMapError(response))

    return response.data as GlobalMapListResponse
  },
  { condition: ({ entities }) => entities.length > 0 }
)

// The Analytics tab's one aggregate fetch — the SAME shared filters/search/
// entities `fetchMapFeatures`/`fetchListRows` already send (spec's own
// "Shared Filters" requirement: Map = List = Analytics filtered
// population), no Analytics-only filter vocabulary. Gated by the identical
// "at least one active layer" `condition` every other entities-aware fetch
// in this file already uses, for the identical reason.
export const fetchAnalytics = createAsyncThunk<
  AnalyticsResponse,
  { filters: GlobalMapFilters; search: string; entities: EntityType[] },
  { rejectValue: GlobalMapError }
>(
  `${globalMapSliceName}/fetchAnalytics`,
  async ({ filters, search, entities }, { rejectWithValue }) => {
    const params: AnalyticsQueryParams = {
      ...buildFilterParams(filters, search),
      entities: entities.join(",")
    }

    const response = await getAnalytics(params)

    if (!response.ok) return rejectWithValue(toGlobalMapError(response))

    return response.data as AnalyticsResponse
  },
  { condition: ({ entities }) => entities.length > 0 }
)

// The Map tab's own "Recent Activity in View" card, below the map canvas —
// mirrors `fetchAnalytics` exactly (same request shape, same
// entities-empty-means-don't-fire condition), against the lighter
// `/recent-activity` endpoint instead of the full Analytics aggregate.
export const fetchRecentActivity = createAsyncThunk<
  RecentActivityByEntityResponse,
  { filters: GlobalMapFilters; search: string; entities: EntityType[] },
  { rejectValue: GlobalMapError }
>(
  `${globalMapSliceName}/fetchRecentActivity`,
  async ({ filters, search, entities }, { rejectWithValue }) => {
    const params: AnalyticsQueryParams = {
      ...buildFilterParams(filters, search),
      entities: entities.join(",")
    }

    const response = await getRecentActivity(params)

    if (!response.ok) return rejectWithValue(toGlobalMapError(response))

    return response.data as RecentActivityByEntityResponse
  },
  { condition: ({ entities }) => entities.length > 0 }
)

// The List tab's Export button (Task 13) — same shared filters/search/
// entities/list_entity/sort `fetchListRows` sends, minus pagination (an
// export is the whole filtered result set, never one page of it), plus an
// optional row-id selection. This thunk owns its own loading/success/error
// feedback end to end (a single keyed `message` that a later call replaces
// rather than stacking) instead of leaving the caller to interpret a
// rejected action — there is no reducer case for it and nothing in
// `GlobalMapState` reflects its outcome.
const EXPORT_MESSAGE_KEY = "global-map-export"

// The backend caps an export at ~5,000 rows and, when it does, sets
// `X-Export-Truncated` (CORS-exposed specifically for this — see
// `config/settings.py`'s own comment: "a client that cannot read them
// downloads a partial file believing it is complete") to a human-readable
// detail string (e.g. "capped at 5000 rows" — `GlobalMapExportView.get` in
// `ezbuild_back`). Case-insensitive lookup mirrors
// `filenameFromContentDisposition`'s own defensive header read just above,
// even though apisauce/axios normally hand back already-lowercased keys.
const exportTruncatedDetail = (
  headers?: Record<string, string>
): string | undefined =>
  Object.entries(headers ?? {}).find(
    ([key]) => key.toLowerCase() === "x-export-truncated"
  )?.[1]

export const exportListRows = createAsyncThunk<
  void,
  {
    filters: GlobalMapFilters
    search: string
    entities: EntityType[]
    listEntity: ListEntityTab
    sort: ListSortField
    direction: "asc" | "desc"
    ids?: string[]
    /** The selected branch workspace, when one is active. */
    team?: string
  }
>(
  `${globalMapSliceName}/exportListRows`,
  async ({
    filters,
    search,
    entities,
    listEntity,
    sort,
    direction,
    ids,
    team
  }) => {
    message.loading({
      content: i18n.t("globalMap.list.export.loading") as string,
      key: EXPORT_MESSAGE_KEY
    })

    try {
      const params: ExportQueryParams = {
        ...buildFilterParams(filters, search),
        entities: entities.join(","),
        list_entity: listEntity,
        sort,
        direction,
        ids,
        ...(team ? { team } : {})
      }

      const response = await exportListRowsRequest(params)

      if (!response.ok || !response.data) {
        message.error({
          content: i18n.t("globalMap.list.export.error") as string,
          key: EXPORT_MESSAGE_KEY
        })
        return
      }

      const filename =
        filenameFromContentDisposition(
          response.headers as Record<string, string> | undefined
        ) ?? `global-map-export-${dayjs().format("YYYY-MM-DD")}.csv`

      // No `message.success` here (Fix 1, task review round) — `downloadBlob`
      // (`utils/download-file.ts`) already shows its own success
      // `notification` unconditionally with no suppress option, so a second,
      // separate toast on top of it double-announced one export. The
      // established precedent this feature was built to follow,
      // `ReportExportMenu.tsx`'s `runExport`, already shows the right shape:
      // it adds no success toast of its own after calling `downloadBlob`,
      // and only adds its own error toast for the two failure paths
      // `downloadBlob` never reaches (the branches above/below, both before
      // any blob exists).
      downloadBlob(response.data, filename)

      // Fix (final-review Important #5) — the file still downloads either
      // way (this never replaces `downloadBlob` above, only follows it);
      // this is purely an additional heads-up for the case the backend
      // built the mechanism for and nothing here used to read.
      const truncatedDetail = exportTruncatedDetail(
        response.headers as Record<string, string> | undefined
      )
      if (truncatedDetail) {
        message.warning({
          content: i18n.t("globalMap.list.export.truncated", {
            detail: truncatedDetail
          }) as string,
          key: EXPORT_MESSAGE_KEY
        })
      }
    } catch {
      message.error({
        content: i18n.t("globalMap.list.export.error") as string,
        key: EXPORT_MESSAGE_KEY
      })
    }
  }
)

export const fetchQuickView = createAsyncThunk<
  EntityQuickView,
  { id: string; entityType: EntityType },
  { rejectValue: GlobalMapError }
>(
  `${globalMapSliceName}/fetchQuickView`,
  async ({ id, entityType }, { rejectWithValue }) => {
    const response = await (entityType === "lead"
      ? getLeadQuickView(id)
      : getProjectQuickView(id))

    if (response.status === 404)
      return rejectWithValue({ code: "not_found", retryable: false })

    if (!response.ok) return rejectWithValue(toGlobalMapError(response))

    return response.data as EntityQuickView
  }
)

// Dispatched from the List tab's overflow "Refresh" menu item
// (`GlobalMapToolbar.tsx`, gated on `activeTab === "list"` before this is
// ever dispatched) — written generically against the slice's own `activeTab`
// rather than hardcoded to the List branch, per the plan's own direction, so
// a future Map-tab refresh control could dispatch the identical thunk. Reads
// every param straight off current state rather than taking any as an
// argument: unlike `fetchListRows`/`fetchMapFeatures` themselves (called with
// explicit params by the hooks that own request-shaping), this thunk's whole
// job is "repeat whichever of those two the current tab already has
// in flight" — same filters/search/layers either tab is already querying
// with.
export const refetchCurrentListOrMapQuery = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  `${globalMapSliceName}/refetchCurrentListOrMapQuery`,
  (_, { dispatch, getState }) => {
    const state = getState().globalMap

    if (state.activeTab === "list") {
      // Fix (final-review Important #1) — this used to hardcode
      // `page: 1, pageSize: LIST_PAGE_SIZE` regardless of where the user
      // actually was, a leftover from before Task 9 introduced
      // `list.currentPage`/`list.pageSize`. Since this thunk dispatches
      // `fetchListRows` directly rather than going through
      // `useListFeatures`'s own effects, nothing corrected the store
      // afterward — a user on page 3 at 50/page who clicked Refresh got
      // page 1's first 10 rows loaded into `list.data` while
      // `ListPaginationFooter` kept rendering "Showing 101–150 of N" from
      // the untouched `currentPage`/`pageSize`. "Refresh" means reload
      // whatever the user is currently looking at, not "jump back to page
      // one", so this now reads both off state — the same values
      // `useListFeatures` itself would fetch with — keeping the request and
      // the displayed pagination state in agreement.
      dispatch(
        fetchListRows({
          filters: state.committedQuery.filters,
          search: state.searchTerm,
          entities: recordLayers(state.committedQuery.layers),
          listEntity: state.list.listEntity,
          sort: state.list.sort,
          direction: state.list.direction,
          page: state.list.currentPage,
          pageSize: state.list.pageSize
        })
      )
      return
    }

    dispatch(
      fetchMapFeatures({
        viewport: state.viewport,
        filters: state.committedQuery.filters,
        search: state.searchTerm,
        entities: state.committedQuery.layers
      })
    )
  }
)
